import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PricingContainer = styled.section`
  padding: 120px 5% 80px;
  background: #000000;
  
  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const PricingContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  margin-bottom: 80px;
  text-align: left;
`;

const SectionTitle = styled(motion.h2)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: clamp(28px, 3.5vw, 40px);
  font-weight: 400;
  color: white;
  line-height: 1.3;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
`;

const SectionSubtitle = styled(motion.p)`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 15px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  max-width: 600px;
  margin: 0;
`;

const PricingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  align-items: start;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`;

const PricingCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 40px 32px;
  text-align: left;
  position: relative;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  ${props => props.popular && `
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.04);
    
    &::before {
      content: 'Most Popular';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: white;
      color: black;
      padding: 6px 16px;
      border-radius: 6px;
      font-size: 12px;
      font-weight: 600;
    }
  `}
  
  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.12);
    transform: translateY(-4px);
  }
`;

const PlanName = styled.h3`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: white;
  margin-bottom: 8px;
`;

const PlanPrice = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 28px;
  font-weight: 600;
  color: white;
  margin-bottom: 8px;
  
  .period {
    font-size: 14px;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.5);
  }
`;

const PlanDescription = styled.p`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.5;
  margin-bottom: 32px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 42px 0;
  flex: 1;
`;

const Feature = styled.li`
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 13px;
  color: ${props => props.empty ? 'transparent' : 'rgba(255, 255, 255, 0.6)'};
  line-height: 1.5;
  margin-bottom: 12px;
  padding-left: 20px;
  position: relative;
  min-height: 20px;
  
  &::before {
    content: ${props => props.empty ? '""' : '"✓"'};
    position: absolute;
    left: 0;
    color: rgba(255, 255, 255, 0.4);
    font-weight: 600;
  }
`;

const PlanButton = styled.button`
  width: 100%;
  background: ${props => props.primary ? 'white' : 'transparent'};
  color: ${props => props.primary ? 'black' : 'white'};
  border: 1px solid ${props => props.primary ? 'white' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 8px;
  padding: 12px 24px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.primary ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.05)'};
    border-color: ${props => props.primary ? 'white' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const Pricing = () => {
  const pricingPlans = [
    {
      name: 'Starter',
      price: 'Free',
      period: '',
      description: 'Perfect for independent artists getting started',
      features: [
        'Connect up to 3 platforms',
        'Basic analytics dashboard',
        'Monthly insights report',
        'Email support',
        '',
        ''
      ],
      buttonText: 'Get Started',
      primary: false
    },
    {
      name: 'Professional',
      price: '$29',
      period: '/month',
      description: 'For serious artists and small teams',
      features: [
        'Unlimited platform connections',
        'Advanced AI insights',
        'Automated workflows',
        'Priority support',
        'Team collaboration',
        'Custom reports'
      ],
      buttonText: 'Start Free Trial',
      primary: true,
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For labels and large organizations',
      features: [
        'Everything in Professional',
        'Custom integrations',
        'Dedicated account manager',
        'Advanced security',
        'SLA guarantee',
        'Custom training'
      ],
      buttonText: 'Contact Sales',
      primary: false
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <PricingContainer>
      <PricingContent>
        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            Simple, transparent pricing
          </SectionTitle>
        </SectionHeader>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <PricingGrid>
            {pricingPlans.map((plan, index) => (
              <PricingCard
                key={index}
                variants={cardVariants}
                popular={plan.popular}
              >
                <PlanName>{plan.name}</PlanName>
                <PlanPrice>
                  {plan.price}
                  {plan.period && <span className="period">{plan.period}</span>}
                </PlanPrice>
                <PlanDescription>{plan.description}</PlanDescription>
                
                <FeatureList>
                  {plan.features.map((feature, featureIndex) => (
                    <Feature key={featureIndex} empty={!feature}>{feature}</Feature>
                  ))}
                </FeatureList>
                
                <PlanButton 
                  primary={plan.primary}
                  onClick={() => window.location.href = '/login'}
                >
                  {plan.buttonText}
                </PlanButton>
              </PricingCard>
            ))}
          </PricingGrid>
        </motion.div>
      </PricingContent>
    </PricingContainer>
  );
};

export default Pricing; 